import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Send, Image, Video, Trash2, X, Camera, Upload, Flag, Trophy } from 'lucide-react';
import LevelBadge, { ExpProgressBar } from '@/components/LevelBadge';
import { compressPostImage } from '@/utils/imageCompression';

const CommunityPosts = ({ user, onBack }) => {
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedCountries, setSelectedCountries] = useState(() => {
    // Load saved countries from localStorage or default to empty (no filter)
    const saved = localStorage.getItem(`community_countries_${user.id}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [sending, setSending] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordingVideo, setRecordingVideo] = useState(false);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [autoScrollDisabled, setAutoScrollDisabled] = useState(false);
  const [userScrollTimeout, setUserScrollTimeout] = useState(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const postsContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const wsRef = useRef(null);
  const textareaRef = useRef(null);
  const selectedCountriesRef = useRef(selectedCountries);

  // Custom background for community posts
  const communityBackground = '/community-posts-background.jpg';

  // Keep ref updated with latest selectedCountries
  useEffect(() => {
    selectedCountriesRef.current = selectedCountries;
  }, [selectedCountries]);

  // Save selected countries to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`community_countries_${user.id}`, JSON.stringify(selectedCountries));
    fetchPosts();
  }, [selectedCountries]);

  useEffect(() => {
    
    // Set up WebSocket for real-time updates
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/community`;
    console.log('Connecting to Community WebSocket:', wsUrl);
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('✓ Community Posts WebSocket connected');
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Received community post update:', data);
      
      // Add new post to the list
      if (data.type === 'new_post') {
        console.log('➕ Adding new post to list:', data.post);
        
        // Check if post matches current country filter (use ref to get latest value)
        const currentFilter = selectedCountriesRef.current;
        const matchesFilter = currentFilter.length === 0 || 
                             currentFilter.includes(data.post.country);
        
        if (!matchesFilter) {
          console.log('Post does not match current country filter, skipping');
          return;
        }
        
        setPosts(prevPosts => {
          // Avoid duplicates
          const exists = prevPosts.some(p => p.id === data.post.id);
          if (exists) {
            console.log('Post already exists, skipping');
            return prevPosts;
          }
          return [data.post, ...prevPosts];
        });
        
        // Only auto-scroll if it's the current user's post and auto-scroll is not disabled
        if (data.post.user_id === user.id && !autoScrollDisabled) {
          setTimeout(() => scrollToBottom('smooth'), 100);
        }
      } else if (data.type === 'delete_post') {
        console.log('🗑️ Removing deleted post:', data.post_id);
        setPosts(prevPosts => prevPosts.filter(p => p.id !== data.post_id));
      }
    };
    
    websocket.onerror = (error) => {
      console.error('❌ Community WebSocket error:', error);
    };
    
    websocket.onclose = () => {
      console.log('🔌 Community WebSocket disconnected');
    };
    
    wsRef.current = websocket;
    
    // Polling as backup for real-time updates (every 5 seconds)
    const pollInterval = setInterval(() => {
      console.log('🔄 Polling for new posts...');
      fetchPosts();
    }, 5000);
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      clearInterval(pollInterval);
      if (userScrollTimeout) {
        clearTimeout(userScrollTimeout);
      }
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom after posts are loaded (only on initial mount)
    if (posts.length > 0) {
      scrollToBottom('auto');
    }
  }, [posts.length > 0]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCountryDropdown && !e.target.closest('.country-dropdown-container')) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCountryDropdown]);

  // Close media options popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMediaOptions && !e.target.closest('.media-options-container')) {
        setShowMediaOptions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMediaOptions]);

  useEffect(() => {
    // Add user interaction listeners (only runs once)
    const container = postsContainerRef.current;
    if (!container) return;

    let isInteracting = false;

    const handleUserInteraction = () => {
      if (isInteracting) return; // Prevent multiple rapid calls
      
      console.log('User interaction detected - disabling auto-scroll for 10 seconds');
      isInteracting = true;
      setAutoScrollDisabled(true);
      
      // Clear existing timeout
      if (userScrollTimeout) {
        clearTimeout(userScrollTimeout);
      }
      
      // Set new timeout to re-enable auto-scroll after 10 seconds
      const timeout = setTimeout(() => {
        console.log('Re-enabling auto-scroll');
        setAutoScrollDisabled(false);
        isInteracting = false;
      }, 10000);
      
      setUserScrollTimeout(timeout);
      
      // Reset interaction flag after a short delay to allow for legitimate interactions
      setTimeout(() => {
        isInteracting = false;
      }, 500);
    };

    // Add event listeners for user interactions
    container.addEventListener('scroll', handleUserInteraction, { passive: true });
    container.addEventListener('touchstart', handleUserInteraction, { passive: true });
    container.addEventListener('wheel', handleUserInteraction, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleUserInteraction);
      container.removeEventListener('touchstart', handleUserInteraction);
      container.removeEventListener('wheel', handleUserInteraction);
    };
  }, []); // Empty dependency array - only run once

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      // Only add countries filter if countries are selected
      if (selectedCountries.length > 0) {
        params.countries = selectedCountries.join(',');
      }
      const response = await axios.get(`${API}/community/posts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      console.log('📥 Fetched posts:', selectedCountries.length === 0 ? 'All countries' : selectedCountries.join(', '), ':', response.data.posts?.length || 0, 'posts');
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Silent fail for polling - don't show toast on every poll failure
    } finally {
      setLoading(false);
    }
  };

  const handleSendPost = async () => {
    if (!newPostContent.trim() && !mediaPreview) {
      toast.error('Please enter a message or add media');
      return;
    }

    setSending(true);
    let postCreated = false;
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        content: newPostContent.trim(),
        image: mediaType === 'image' ? mediaPreview : null,
        video: mediaType === 'video' ? mediaPreview : null,
        country: selectedCountries[0] || 'United States'  // Use first selected country for posting
      };

      // Post creation
      const response = await axios.post(`${API}/community/posts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      postCreated = true; // Mark that post was successfully created
      console.log('✅ Post created successfully:', response.data);

      setNewPostContent('');
      setMediaPreview(null);
      setMediaType(null);
      toast.success('Post shared!');
      
      // Refresh posts immediately to show the new post
      // If this fails, don't show error since post was already created successfully
      try {
        await fetchPosts();
      } catch (fetchError) {
        console.warn('Post created but failed to refresh list. WebSocket will update it.', fetchError);
        // Silent fail - WebSocket will broadcast the new post anyway
      }
      
      // Auto-scroll to show the new post (regardless of disabled state for own posts)
      setTimeout(() => scrollToBottom('smooth'), 200);
    } catch (error) {
      console.error('Error sending post:', error);
      // Only show error if post creation actually failed
      if (!postCreated) {
        if (error.code === 'ECONNABORTED') {
          toast.error('Post upload timed out. Try with smaller media.');
        } else if (error.response?.status === 400) {
          toast.error(error.response.data.detail || 'Invalid post content');
        } else {
          toast.error('Failed to send post');
        }
      }
    } finally {
      setSending(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleFlagPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/community/posts/${postId}/flag`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.deleted) {
        toast.success('Post has been removed');
      } else {
        toast.success('Post has been flagged');
      }
    } catch (error) {
      console.error('Error flagging post:', error);
      if (error.response?.data?.detail === 'You have already flagged this post') {
        toast.error('You have already flagged this post');
      } else {
        toast.error('Failed to flag post');
      }
    }
  };


  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      // Compress image before preview (max 200KB)
      const compressedFile = await compressPostImage(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
        setMediaType('image');
        setShowMediaOptions(false);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fall back to original file
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
        setMediaType('image');
        setShowMediaOptions(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be less than 50MB');
      return;
    }

    // Show loading toast
    const loadingToast = toast.loading('Processing video...');

    try {
      // Convert video to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
        setMediaType('video');
        setShowMediaOptions(false);
        toast.dismiss(loadingToast);
        toast.success('Video ready to send');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to process video');
    }
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoRef.current.srcObject = stream;
      videoRef.current.play();

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const reader = new FileReader();
        reader.onloadend = () => {
          setMediaPreview(reader.result);
          setMediaType('video');
          setRecordingVideo(false);
          setShowMediaOptions(false);
        };
        reader.readAsDataURL(blob);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecordingVideo(true);
    } catch (error) {
      console.error('Error starting video recording:', error);
      toast.error('Failed to start video recording');
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className="min-h-screen bg-slate-950 flex flex-col relative"
      style={{
        backgroundImage: `url(${communityBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Posts Area */}
      <div ref={postsContainerRef} className="relative z-10 flex-1 overflow-y-auto p-4 space-y-4 pt-20">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-cyan-400">Loading posts...</div>
          </div>
        ) : posts.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-slate-400">
              <p className="text-lg mb-2">No posts yet</p>
              <p className="text-sm">Be the first to share something!</p>
            </div>
          </div>
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              className="bg-black/20 backdrop-blur-sm rounded-lg p-4 space-y-3"
            >
              {/* Post Header - Leaderboard Style */}
              <div className="space-y-3">
                {/* Username Banner with Battle Points - Exact Leaderboard Style */}
                <div 
                  className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 flex justify-between items-center relative"
                  style={(post.user_banner_image && post.user_banner_image.trim() !== '') ? {
                    backgroundImage: `url(${post.user_banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {}}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {post.user_banner_image && post.user_banner_image.trim() !== '' && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                  )}
                  
                  {/* Level Badge */}
                  <LevelBadge level={post.level || 1} small={true} verified={post.has_made_first_purchase} />
                  
                  <div className="flex items-center gap-3 relative z-10">
                    {/* Profile Picture */}
                    {post.user_profile_picture ? (
                      <img
                        src={post.user_profile_picture}
                        alt={post.username}
                        className="w-12 h-12 rounded-full object-cover border-2 pointer-events-none"
                        style={{ borderColor: post.circle_color || '#475569' }}
                      />
                    ) : (
                      <div 
                        className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold pointer-events-none"
                        style={{ borderColor: post.circle_color || '#475569' }}
                      >
                        {post.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className={`font-medium ${(post.user_banner_image && post.user_banner_image.trim() !== '') ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                      {post.username}
                    </span>
                  </div>
                  
                  {/* Battle Points and Action Button */}
                  <div className="flex items-center gap-2 relative z-10">
                    <span className={`digital-font font-semibold ${(post.user_banner_image && post.user_banner_image.trim() !== '') ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                      {post.battle_points || 0} BP
                    </span>
                    
                    {/* Delete/Flag Button */}
                    {post.user_id === user.id ? (
                      <Button
                        onClick={() => handleDeletePost(post.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleFlagPost(post.id)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 h-8 w-8 p-0"
                        title="Flag post"
                      >
                        <Flag className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Trophy System - Top Right */}
                  <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-yellow-500" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{post.gold_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-slate-300" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{post.silver_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-amber-600" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{post.bronze_trophies || 0}</span>
                    </div>
                  </div>
                  
                  {/* EXP Progress Bar */}
                  <ExpProgressBar level={post.level || 1} exp={post.exp || 0} />
                </div>

                {/* Post Content */}
                {post.content && (
                  <div className="text-white whitespace-pre-wrap">{post.content}</div>
                )}

                {/* Post Media */}
                {post.image && (
                  <img
                    src={post.image}
                    alt="Post"
                    className="w-full rounded-lg max-h-96 object-contain bg-slate-900"
                  />
                )}
                {post.video && (
                  <video
                    src={post.video}
                    controls
                    className="w-full rounded-lg max-h-96 bg-slate-900"
                  />
                )}

                {/* Timestamp below content and media */}
                <div className="text-xs text-slate-400">{formatTimestamp(post.created_at)}</div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Bottom Bar with Back Button and Input Area */}
      <div className="relative z-20 bg-slate-900/90 backdrop-blur-md border-t border-slate-700">
        {/* Back Button */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 border-b border-slate-700/50">
          <button
            onMouseDown={(e) => {
              // Use mouseDown/touchStart instead of onClick to check focus BEFORE blur fires
              e.preventDefault(); // Prevent blur from firing
              
              // Check if textarea is currently the active element
              const isTextareaActive = document.activeElement === textareaRef.current;
              
              if (isTextareaActive && textareaRef.current) {
                console.log('Textarea focused, blurring first');
                textareaRef.current.blur();
                setIsTextareaFocused(false);
              } else {
                console.log('Textarea not focused, going back to dashboard');
                onBack();
              }
            }}
            onTouchStart={(e) => {
              // Handle touch events for mobile
              e.preventDefault(); // Prevent blur from firing
              
              // Check if textarea is currently the active element
              const isTextareaActive = document.activeElement === textareaRef.current;
              
              if (isTextareaActive && textareaRef.current) {
                console.log('Textarea focused (touch), blurring first');
                textareaRef.current.blur();
                setIsTextareaFocused(false);
              } else {
                console.log('Textarea not focused (touch), going back to dashboard');
                onBack();
              }
            }}
            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg p-2 transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-8 h-8" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">Community Posts</h2>
            <p className="text-xs text-slate-400">Share with all bladers</p>
          </div>
          <div className="relative country-dropdown-container">
            <button
              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              className="bg-slate-800 border border-slate-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-slate-700"
            >
              {selectedCountries.length === 0 ? 'All Countries' : selectedCountries.length === 1 ? selectedCountries[0] : `${selectedCountries.length} Countries`}
            </button>
            {showCountryDropdown && (
              <div className="fixed right-4 bottom-32 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl z-[9999] max-h-80 overflow-y-auto w-56">
                {['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Spain', 'Italy', 'Japan', 'South Korea', 'China', 'Taiwan', 'India', 'Philippines', 'Brazil', 'Mexico', 'Argentina', 'Other'].map(country => (
                  <label
                    key={country}
                    className="flex items-center gap-2 px-4 py-2 hover:bg-slate-700 cursor-pointer text-sm text-white"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCountries([...selectedCountries, country]);
                        } else {
                          setSelectedCountries(selectedCountries.filter(c => c !== country));
                        }
                      }}
                      className="rounded"
                    />
                    {country}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="p-4">
        {/* Media Preview */}
        {mediaPreview && (
          <div className="mb-3 relative">
            <button
              onClick={() => {
                setMediaPreview(null);
                setMediaType(null);
              }}
              className="absolute top-2 right-2 z-10 bg-slate-900/80 backdrop-blur-sm rounded-full p-1 text-white hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
            {mediaType === 'image' ? (
              <img
                src={mediaPreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-lg bg-slate-800"
              />
            ) : (
              <video
                src={mediaPreview}
                controls
                className="w-full max-h-48 rounded-lg bg-slate-800"
              />
            )}
          </div>
        )}

        {/* Video Recording View */}
        {recordingVideo && (
          <div className="mb-3 relative">
            <video
              ref={videoRef}
              className="w-full max-h-48 rounded-lg bg-slate-800"
              autoPlay
              muted
            />
            <Button
              onClick={stopVideoRecording}
              className="absolute bottom-2 right-2 bg-red-500 hover:bg-red-600 text-white"
            >
              Stop Recording
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              onFocus={() => setIsTextareaFocused(true)}
              onBlur={() => setIsTextareaFocused(false)}
              placeholder="Share something with the community..."
              className="w-full bg-slate-800 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              rows="3"
              disabled={sending}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          {/* Media Options */}
          <div className="relative media-options-container">
            <Button
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              variant="ghost"
              size="sm"
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              <Image className="w-5 h-5" />
            </Button>
            {showMediaOptions && (
              <div className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-2 space-y-1 min-w-[150px]">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-white hover:bg-slate-700 rounded-lg"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">Upload Photo</span>
                </button>
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-white hover:bg-slate-700 rounded-lg"
                >
                  <Video className="w-4 h-4" />
                  <span className="text-sm">Upload Video</span>
                </button>
                <button
                  onClick={startVideoRecording}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-white hover:bg-slate-700 rounded-lg"
                >
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">Record Video</span>
                </button>
              </div>
            )}
          </div>

          <Button
            onClick={handleSendPost}
            disabled={sending || (!newPostContent.trim() && !mediaPreview)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white ml-auto"
          >
            {sending ? 'Posting...' : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Post
              </>
            )}
          </Button>
        </div>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        className="hidden"
      />
    </div>
  );
};

export default CommunityPosts;
